<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { api, messageFrom } from '../api'
import { formatDuration } from '../format'
import type { SpeakerSummary } from '../types'

const emit = defineEmits<{ navigate: [path: string] }>()
const speakers = ref<SpeakerSummary[]>([])
const name = ref('')
const loading = ref(true)
const saving = ref(false)
const error = ref('')

async function load(): Promise<void> {
  try { speakers.value = (await api<{ speakers: SpeakerSummary[] }>('/api/admin/speakers')).speakers }
  catch (reason) { error.value = messageFrom(reason) }
  finally { loading.value = false }
}

async function create(): Promise<void> {
  if (!name.value.trim() || saving.value) return
  saving.value = true
  error.value = ''
  try {
    const response = await api<{ speaker: SpeakerSummary }>('/api/admin/speakers', {
      method: 'POST', body: JSON.stringify({ name: name.value }),
    })
    name.value = ''
    emit('navigate', `/admin/speakers/${response.speaker.id}`)
  } catch (reason) { error.value = messageFrom(reason) }
  finally { saving.value = false }
}

onMounted(load)
</script>

<template>
  <main class="page">
    <header class="page-heading">
      <div>
        <p class="eyebrow">Архив</p>
        <h1>Папки спикеров</h1>
      </div>
    </header>

    <form class="new-folder" @submit.prevent="create">
      <label for="speaker-name">Новая папка</label>
      <div class="inline-form">
        <input id="speaker-name" v-model="name" placeholder="Имя спикера" maxlength="160" />
        <button class="button button--primary" type="submit" :disabled="saving || !name.trim()">
          {{ saving ? 'Создаём…' : 'Создать' }}
        </button>
      </div>
      <p v-if="error" class="field-error" role="alert">{{ error }}</p>
    </form>

    <p v-if="loading" class="quiet-message">Загружаем папки…</p>
    <p v-else-if="!speakers.length" class="empty-state">
      Пока нет ни одной папки. Создайте папку с именем первого спикера.
    </p>
    <div v-else class="speaker-grid">
      <button v-for="speaker in speakers" :key="speaker.id" class="speaker-card" @click="emit('navigate', `/admin/speakers/${speaker.id}`)">
        <strong>{{ speaker.name }}</strong>
        <span>{{ speaker.recordingCount }} {{ speaker.recordingCount === 1 ? 'запись' : 'записей' }}</span>
        <dl>
          <div><dt>Длительность</dt><dd>{{ formatDuration(speaker.totalDurationSec) }}</dd></div>
          <div><dt>Расшифровано</dt><dd>{{ speaker.transcribedCount }} из {{ speaker.recordingCount }}</dd></div>
        </dl>
      </button>
    </div>
  </main>
</template>
